import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Expand, EyeOff, PictureInPicture2, Volume2, VolumeX } from 'lucide-react'
import { cn } from '@mriqbox/ui-kit'

export interface TileMenuAction {
    id: string
    label: string
    icon: 'hide' | 'fullscreen' | 'pip' | 'audio-on' | 'audio-off'
    onSelect: () => void
    /** Marca a opcao como ativa (som ligado, ja na janela flutuante). */
    active?: boolean
}

export interface TileMenuProps {
    /** Onde o menu nasce, em coordenadas da janela. */
    x: number
    y: number
    actions: TileMenuAction[]
    onClose: () => void
}

const ICONS = {
    hide: EyeOff,
    fullscreen: Expand,
    pip: PictureInPicture2,
    'audio-on': Volume2,
    'audio-off': VolumeX
} as const

/**
 * Menu do botao direito sobre uma transmissao.
 *
 * Vai por portal no `body` porque o tile tem `overflow: hidden` (o zoom depende
 * disso): renderizado dentro dele, o menu seria cortado na borda.
 */
export function TileMenu({ x, y, actions, onClose }: TileMenuProps) {
    const ref = useRef<HTMLDivElement>(null)
    const [posicao, setPosicao] = useState({ x, y })

    /**
     * Empurra pra dentro da janela ANTES de pintar.
     *
     * Aberto perto da borda direita ou de baixo — que e onde ficam os tiles do
     * mosaico —, o menu nasceria metade fora da tela. `useLayoutEffect` corrige
     * antes do primeiro quadro, senao ele apareceria fora e pularia pro lugar.
     */
    useLayoutEffect(() => {
        const el = ref.current
        if (!el) return
        const { width, height } = el.getBoundingClientRect()
        const margem = 8
        setPosicao({
            x: Math.min(x, window.innerWidth - width - margem),
            y: Math.min(y, window.innerHeight - height - margem)
        })
    }, [x, y])

    useEffect(() => {
        const fechar = () => onClose()
        const noEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        // `pointerdown` em vez de `click`: fecha assim que a pessoa encosta
        // fora, sem esperar o botao subir.
        window.addEventListener('pointerdown', fechar)
        window.addEventListener('keydown', noEsc)
        // Rolar ou redimensionar move o tile embaixo do menu: melhor fechar.
        window.addEventListener('resize', fechar)
        window.addEventListener('scroll', fechar, true)
        return () => {
            window.removeEventListener('pointerdown', fechar)
            window.removeEventListener('keydown', noEsc)
            window.removeEventListener('resize', fechar)
            window.removeEventListener('scroll', fechar, true)
        }
    }, [onClose])

    return createPortal(
        <div
            ref={ref}
            role="menu"
            style={{ left: posicao.x, top: posicao.y }}
            // O menu e do proprio ponteiro que acabou de descer: sem isto, o
            // `pointerdown` que o abriu fecharia ele em seguida.
            onPointerDown={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.preventDefault()}
            className="fixed z-50 min-w-44 overflow-hidden rounded-xl border border-border/80 bg-card/95 p-1 shadow-2xl shadow-black/50 backdrop-blur"
        >
            {actions.map((action) => {
                const Icon = ICONS[action.icon]
                return (
                    <button
                        key={action.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            action.onSelect()
                            onClose()
                        }}
                        className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent',
                            action.active && 'text-primary'
                        )}
                    >
                        <Icon className="h-4 w-4 shrink-0" />
                        {action.label}
                    </button>
                )
            })}
        </div>,
        document.body
    )
}
